import api from '@disco/api/server'
import {
  CONTACTS_CREATE_SUCCESS,
  EDITED_TRACK_INIT,
  EDITED_TRACK_UPDATE,
  PLAYLIST_DELETE_BATCH,
  PLAYLIST_DELETE_BATCH_ERROR,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_LOAD_PLAIN_UPDATE,
  PLAYLIST_METADATA_SAVE,
  PLAYLIST_METADATA_SAVE_ERROR,
  PLAYLIST_METADATA_SAVE_SUCCESS,
  PLAYLIST_VERSION_DELETE,
  PLAYLIST_VERSION_DELETE_ERROR,
  PLAYLIST_VERSION_DELETE_SUCCESS,
  SWITCH_TERRITORY_WARNING,
  TRACK_ADD_WRITER,
  TRACK_ADD_WRITER_FAIL,
  TRACK_ADD_WRITER_SUCCESS,
  TRACK_DELETE_BATCH,
  TRACK_DELETE_BATCH_ERROR,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_DELETE_WRITER,
  TRACK_DELETE_WRITER_FAIL,
  TRACK_DELETE_WRITER_SUCCESS,
  TRACK_FORMAT_DELETE,
  TRACK_FORMAT_DELETE_ERROR,
  TRACK_FORMAT_DELETE_SUCCESS,
  TRACK_GROUP_CHANGE,
  TRACK_GROUP_CHANGE_ERROR,
  TRACK_GROUP_CHANGE_SUCCESS,
  TRACK_LOAD_BATCH,
  TRACK_METADATA_FETCH,
  TRACK_METADATA_FETCH_ERROR,
  TRACK_METADATA_FETCH_SUCCESS,
  TRACK_METADATA_SAVE,
  TRACK_METADATA_SAVE_ERROR,
  TRACK_METADATA_SAVE_SUCCESS,
  TRACK_UPDATE_WRITER,
  TRACK_UPDATE_WRITER_FAIL,
  TRACK_UPDATE_WRITER_SUCCESS,
  TRACKS_TRANSCRIBE,
  TRANSCRIPTION_FEEDBACK_SEND,
} from '@disco/constants/ActionTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { getSupplyChannelGroups } from '@disco/state-management/selectors/groups'
import {
  getPlaylistIdsAllowedForRemoval,
  getPlaylistsByIds,
} from '@disco/state-management/selectors/playlists'
import {
  getTrackIdsAllowedForRemoval,
  getTrackIdsByNestingParentId,
  getTracksByIds,
  getTracksByNestingParentId,
} from '@disco/state-management/selectors/tracks'
import { parseServerError } from '@disco/utils/captureError'
import { convertMixedMetadataToBasicMetadata } from '@disco/utils/commonMetadata'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { getCommonGroups, getGroupIdFromUrl } from '@disco/utils/groups'
import { pushUINotification } from '@disco/utils/notifications'
import { mediaItemsToString } from '@disco/utils/string-utils'
import omit from 'lodash/omit'
import NavigationService from '@disco/state-management/NavigationService'
import type { Group } from 'types/group'
import type { Playlist, PlaylistId, PlaylistVersionId } from 'types/playlist'
import type { GetState, Thunk } from 'types/redux'
import { updateBusinessPrivateMetadata } from '@disco/state-management/actions/UserActions'

import type {
  EditableTrack,
  EditableTrackMetadata,
  Track,
  TrackId,
} from 'types/track'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  addPro,
  addPublishers,
} from '@disco/state-management/actions/rights-data-actions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  handleLyricsComplete,
  handleLyricsStarting,
} from '@disco/state-management/actions/LyricActions'
import {
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'

// Result of a server track/playlist delete. resolved (never rejected) so
// frontend callers that discard the return value don't trigger unhandled
// promise rejections.
type DeleteResult = { success: boolean; error?: unknown }
/**
 * Shows or hides a territory switching warning message.
 * @param status - Whether to show the territory warning
 * @returns Action object to update territory warning status
 */
export const switchTerritoryWarning = (
  status: boolean,
): {
  status: boolean
  type: string
} => ({
  // TODO: after v1 sunset delete `switchTerritoryWarning` (unused)
  type: SWITCH_TERRITORY_WARNING,
  status,
})
/**
 * Initializes a track for editing in the track editor.
 * @param editedTrack - The track data to initialize for editing
 * @returns Action object to initialize edited track state
 */
export const initEditedTrack = (
  editedTrack: EditableTrack,
): {
  editedTrack: EditableTrack
  type: string
} => ({
  type: EDITED_TRACK_INIT,
  editedTrack,
})
/**
 * Updates the currently edited track with new data.
 * @param editedTrack - Updated track data for the track being edited
 * @returns Action object to update edited track state
 */
export const updateEditedTrack = (
  editedTrack: EditableTrack,
): {
  editedTrack: EditableTrack
  type: string
} => ({
  type: EDITED_TRACK_UPDATE,
  editedTrack,
})
/**
 * Saves playlist metadata changes to the server.
 * @param playlist - The playlist to update
 * @param metadata - Partial playlist metadata to update
 * @returns Thunk that saves metadata and dispatches success/error actions
 */
export function savePlaylistMetadata(
  playlist: Playlist,
  metadata: Partial<Playlist>,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: PLAYLIST_METADATA_SAVE,
    })
    return api
      .updatePlaylistMetadata(playlist, metadata)
      .then(result => {
        mapResponseToReducerObjects(dispatch, getState, result)
        return dispatch({
          type: PLAYLIST_METADATA_SAVE_SUCCESS,
        })
      })
      .catch(error =>
        dispatch({
          type: PLAYLIST_METADATA_SAVE_ERROR,
          error,
        }),
      )
  }
}
/**
 * Updates metadata for multiple tracks at once, including artwork.
 * @param source_track_id - ID of the source track to copy metadata from
 * @param tracks - Array of tracks to update
 * @param metadata - New metadata to apply to all tracks
 * @param options - Additional options including image file for artwork
 * @param options.imageFile - Optional image file to use as artwork
 * @returns Thunk that updates metadata for all specified tracks
 */
export function bulkUpdateTrackMetadata(
  source_track_id: TrackId | null | undefined,
  tracks: Track[],
  metadata: EditableTrackMetadata,
  options: {
    imageFile?: File | null | undefined
  } = {},
): (dispatch: any, getState: GetState) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      const freshTracks = getState().tracks
      dispatch({
        type: TRACK_METADATA_SAVE,
      })
      const convertedMetadata = convertMixedMetadataToBasicMetadata(metadata)
      const newMetadata = omit(convertedMetadata, ['Artwork', 'Lyrics_source'])
      const track_ids = tracks.map(track => track.id)
      let artworkMetadata = metadata.Artwork

      if (artworkMetadata?.value === 'IMAGE_FILE' && options?.imageFile) {
        artworkMetadata = { ...artworkMetadata, value: options?.imageFile }
      }

      const artworkIsFile = Boolean(
        artworkMetadata?.value &&
          typeof artworkMetadata?.value !== 'string' &&
          artworkMetadata?.value?.type,
      )
      let artworkUpdateResponse: any = null

      if (
        !artworkMetadata?.mixed &&
        (artworkIsFile || artworkMetadata?.value === null)
      ) {
        // update artwork only on video and audio files,
        // ignore docs and images
        const allowedArtworkTrackIds = tracks
          .filter(track => {
            const type = getFileTypeByExt(track.ext)
            return type === AUDIO_FILE || type === VIDEO_FILE
          })
          .map(track => track.id)

        if (
          allowedArtworkTrackIds.length > 0 &&
          typeof artworkMetadata?.value !== 'string'
        ) {
          artworkUpdateResponse = await api.batchUpdateTrackArtwork(
            allowedArtworkTrackIds,
            artworkMetadata?.value,
          )
        }
      }

      Object.keys(newMetadata).forEach(key => {
        if (Number.isNaN(newMetadata[key])) {
          // https://app.asana.com/0/35126173840533/529102680934100/f
          delete newMetadata[key]
        }
      })
      await api.bulkUpdateTrackMetadata(source_track_id, track_ids, {
        metadata: newMetadata,
      })
      // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
      const newAttrs: Partial<Omit<Track, 'name'>> & { name?: string | null } =
        {}
      if ('Title' in newMetadata) newAttrs.name = newMetadata.Title
      if ('Album' in newMetadata) newAttrs.album = newMetadata.Album
      if ('Artist' in newMetadata) newAttrs.artist = newMetadata.Artist
      const updatedTracks = tracks.map(track => {
        const updatedTrack = {
          ...track,
          // don't touch the comments - they could be stale when in explorer
          comments: freshTracks[track.id].comments,
          ...newAttrs,
          metadata: { ...track.metadata, ...newMetadata },
        }

        // artwork was updated
        if (artworkUpdateResponse) {
          const type = getFileTypeByExt(track.ext)
          const ignoredType = !(type === AUDIO_FILE || type === VIDEO_FILE)

          // TODO: refactor copyMetadata/bulkUpdateTrackMetadata duplication
          if (!artworkUpdateResponse.artwork || ignoredType) {
            return omit(updatedTrack, ['artwork', 'artwork_sizes'])
          } else {
            return { ...updatedTrack, ...artworkUpdateResponse }
          }
        }

        return updatedTrack
      })
      dispatch(loadBatch(updatedTracks))
      pushUINotification({
        message: 'Saved!',
        level: 'success',
      })
      return dispatch({
        type: TRACK_METADATA_SAVE_SUCCESS,
      })
    } catch (error) {
      dispatch({
        type: TRACK_METADATA_SAVE_ERROR,
        error,
      })
      throw error
    }
  }
}
/**
 * Deletes multiple tracks from the system with optional nested track handling.
 * @param ids - Array of track IDs to delete
 * @param source - Source context for the deletion (for analytics)
 * @param pvId - Playlist version ID if deleting from a specific playlist
 * @param deleteNested - Whether to also delete nested tracks (versions/stems)
 * @returns Thunk that deletes tracks and handles locked/supply channel tracks
 */
export function bulkDeleteTracks(
  ids: Array<TrackId>,
  source: any,
  pvId: PlaylistVersionId | null | undefined,
  deleteNested?: boolean | null | undefined,
): Thunk {
  return (dispatch, getState) => {
    const state = getState()
    const trackIds = getTrackIdsAllowedForRemoval(state, {
      ids,
    })
    let nestedTrackIds: TrackId[] = []

    if (deleteNested === true) {
      nestedTrackIds = trackIds.flatMap((id: TrackId) =>
        getTrackIdsByNestingParentId(state, {
          id,
        }),
      )
    }

    // Captured so callers (e.g. the mobile delete-track mutation) can await the
    // server delete and check `success`; defaults to a resolved promise when
    // there is nothing to delete.
    let deletePromise: Promise<DeleteResult> = Promise.resolve({
      success: true,
    })

    if (trackIds.length > 0) {
      dispatch({
        type: TRACK_DELETE_BATCH,
        trackIds,
      })
      deletePromise = api
        .bulkDeleteTracks(trackIds, source, pvId, deleteNested)
        .then(() => {
          dispatch({
            type: TRACK_DELETE_BATCH_SUCCESS,
            trackIds: [...trackIds, ...nestedTrackIds],
          })

          if (deleteNested === false) {
            const updatedTracks = trackIds.flatMap((id: TrackId) =>
              getTracksByNestingParentId(state, {
                id,
              }).map((track: Track) => {
                return {
                  ...track,
                  nesting_label: null,
                  nesting_parent_id: null,
                  nesting_position: null,
                }
              }),
            )
            dispatch({
              type: TRACK_LOAD_BATCH,
              list: updatedTracks,
            })
          }

          pushUINotification({
            message: 'Removed from DISCO!',
            level: 'success',
          })
          const eventMessage =
            trackIds.length > 1
              ? 'Delete multiple selected tracks'
              : 'Delete track'
          dispatch(sendMixpanelEvent(eventMessage))

          return { success: true }
        })
        .catch(error => {
          dispatch({
            type: TRACK_DELETE_BATCH_ERROR,
            error,
          })

          return { success: false, error }
        })
    }

    // tracks that cant'be deleted, should be excluded from not locked groups
    const supplyChannelTrackIds = ids.filter(id => !trackIds.includes(id))

    if (supplyChannelTrackIds.length > 0) {
      const supplyChannelTracks = getTracksByIds(state, {
        ids: supplyChannelTrackIds,
      })
      dispatch(removeLockedTracks(supplyChannelTracks))
    }

    return deletePromise
  }
}
/**
 * Removes items from supply channels when they can't be fully deleted.
 * Generic function for handling both tracks and playlists.
 * @param supplyChannelItems - Items that are locked in supply channels
 * @param filter - Optional filter function to apply to items
 * @param apiCall - API function to call for removal
 * @param action - Redux action creator for updating state
 * @returns Thunk that removes items from supply channels
 */
export function removeLockedItems<T extends Playlist | Track>(
  supplyChannelItems: T[],
  filter: ((items: T[]) => T[]) | null | undefined,
  apiCall: (items: T[]) => Promise<any>,
  action: (items: T[]) => Record<string, any>,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const filteredItems = filter
      ? filter(supplyChannelItems)
      : supplyChannelItems
    const state = getState()
    const supplyGroupIds = getSupplyChannelGroups(state).map(g => g.id)

    try {
      await apiCall(filteredItems)
      const updatedItems = filteredItems.map(t => {
        return {
          ...t,
          groups: t.groups.filter(groupUrl => {
            const groupId = getGroupIdFromUrl(groupUrl)
            if (groupId !== null && groupId !== undefined) {
              return supplyGroupIds.includes(groupId)
            }

            return false
          }),
        }
      })
      dispatch(action(updatedItems))
    } catch (error) {
      if (FRONTEND_VERSION === 'v2') {
        const type =
          filteredItems[0] && filteredItems[0].type === TRACK
            ? 'track'
            : 'playlist'
        const intercomMsg = `I’m having trouble removing ${mediaItemsToString(
          filteredItems,
          type === 'playlist' ? 'playlist_id' : 'id',
        )} from Browse.`
        pushUINotification({
          message: `There was an error removing the ${mediaItemsToString(
            filteredItems,
          )} from Browse. Click |${JSON.stringify({
            retryMessage: 'here',
          })}| to try again. If you’re still having trouble, |${JSON.stringify({
            contactUsLink: 'let us know.',
            intercomMsg: intercomMsg,
          })}|`,
          retry: () =>
            dispatch(
              removeLockedItems<T>(supplyChannelItems, filter, apiCall, action),
            ),
          level: 'error',
          dismissible: 'button',
        })
      } else {
        pushUINotification({
          message: parseServerError(error.body),
          level: 'error',
        })
      }

      return dispatch(sendGenericError(error))
    }
  }
}
/**
 * Removes tracks from supply channels when they can't be fully deleted.
 * @param supplyChannelTracks - Tracks that are locked in supply channels
 * @returns Thunk that removes tracks from browse/supply channels
 */
export function removeLockedTracks(
  supplyChannelTracks: Track[],
): (dispatch: any, getState: GetState) => Promise<void> | Promise<unknown> {
  return removeLockedItems<Track>(
    supplyChannelTracks,
    items => items.filter(t => t.uploaded),
    items => {
      return api.removeTracksFromBrowse(items.map(t => t.id))
    },
    items => ({
      type: TRACK_LOAD_BATCH,
      list: items,
    }),
  )
}
/**
 * Removes playlists from supply channels when they can't be fully deleted.
 * @param supplyChannelPlaylists - Playlists that are locked in supply channels
 * @returns Thunk that removes playlists from browse/supply channels
 */
export function removeLockedPlaylists(
  supplyChannelPlaylists: Playlist[],
): (dispatch: any, getState: GetState) => Promise<void> | Promise<unknown> {
  return removeLockedItems<Playlist>(
    supplyChannelPlaylists,
    null,
    items => {
      return api.removePlaylistsFromBrowse(items.map(p => p.playlist_id))
    },
    items => ({
      type: PLAYLIST_LOAD_PLAIN_UPDATE,
      playlists: items,
    }),
  )
}
/**
 * Deletes a single track from the system.
 * @param params - Object containing track parameters
 * @param params.id - ID of the track to delete
 * @param deleteNested - Whether to also delete nested tracks (versions/stems)
 * @returns Thunk that deletes the track using bulk delete functionality
 */
export function deleteTrack(
  {
    id,
  }: {
    id: TrackId
  },
  deleteNested?: boolean | null | undefined,
): Thunk {
  return bulkDeleteTracks(
    [id],
    'Delete action from track context menu',
    null,
    deleteNested,
  )
}
/**
 * Deletes multiple playlists from the system.
 * @param ids - Array of playlist IDs to delete
 * @returns Thunk that deletes playlists and handles locked/supply channel playlists
 */
export function bulkDeletePlaylists(ids: PlaylistId[]): Thunk {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const state = getState()
    const playlistIds = getPlaylistIdsAllowedForRemoval(state, {
      ids,
    })
    if (playlistIds.length === 0) return Promise.resolve({ success: true })
    dispatch({
      type: PLAYLIST_DELETE_BATCH,
    })
    const deletePromise: Promise<DeleteResult> = api
      .bulkDeletePlaylists(playlistIds)
      .then(() => {
        dispatch({
          type: PLAYLIST_DELETE_BATCH_SUCCESS,
          playlistIds,
        })
        pushUINotification({
          message: 'Removed from DISCO!',
          level: 'success',
        })
        const eventMessage =
          playlistIds.length > 1
            ? 'Delete multiple selected playlists'
            : 'Delete playlist'
        dispatch(sendMixpanelEvent(eventMessage))

        return { success: true }
      })
      .catch(error => {
        dispatch({
          type: PLAYLIST_DELETE_BATCH_ERROR,
          error,
        })

        return { success: false, error }
      })
    // playlists that cant'be deleted, should be excluded from not locked groups
    const supplyChannelPlaylistIds = ids.filter(id => !playlistIds.includes(id))

    if (supplyChannelPlaylistIds.length > 0) {
      const supplyChannelPlaylists = getPlaylistsByIds(state, {
        ids: supplyChannelPlaylistIds,
      }).filter(Boolean)
      dispatch(removeLockedPlaylists(supplyChannelPlaylists))
    }

    return deletePromise
  }
}
/**
 * Deletes a single playlist from the system.
 * @param playlist - The playlist to delete
 * @returns Thunk that deletes the playlist using bulk delete functionality
 */
export function deletePlaylist(playlist: Playlist): Thunk {
  return bulkDeletePlaylists([playlist.playlist_id])
}
/**
 * Fetches detailed metadata for a track including comments and writers.
 * @param trackId - ID of the track to fetch metadata for
 * @returns Thunk that fetches and loads track metadata
 */
export function fetchTrackMetadata(
  trackId: TrackId,
): (dispatch: any, getState: GetState) => Promise<Track | undefined> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: TRACK_METADATA_FETCH,
    })
    const track = getState().tracks[trackId]
    let trackPromise

    if (track?.internal_data?.is_aggregated_search_track) {
      trackPromise = api.getAggregatedTrackByUrl(track.url)
    } else {
      trackPromise = api.getTrack(trackId)
    }

    return trackPromise.then(
      resp => {
        const track = mapResponseToReducerObjects(dispatch, getState, resp)
        dispatch({
          type: TRACK_METADATA_FETCH_SUCCESS,
          tracksComments: [track.comments],
          trackWriters: [track.track_writers],
        })
        return track
      },
      error => {
        dispatch({
          type: TRACK_METADATA_FETCH_ERROR,
          error,
        })
      },
    )
  }
}
/**
 * Initiates lyric transcription for multiple tracks using AI.
 * @param trackIds - Array of track IDs to transcribe
 * @returns Thunk that starts transcription and handles results/failures
 */
export function transcribeTracks(trackIds: TrackId[]): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: TRACKS_TRANSCRIBE,
      trackIds,
    })
    const response = await api.transcribeTracks(trackIds)
    const result: {
      pending: Track[]
      failures: {
        track: Track
        message?: string
      }[]
    } = Object.keys(response.results).reduce<{
      failures: {
        track: Track
        message?: string
      }[]
      pending: Track[]
    }>(
      (acc, trackId) => {
        const id = Number(trackId)
        const trackResponse = response.results[id]
        const track = getState().tracks[trackId]

        if (!trackResponse?.success) {
          acc.failures.push({
            track,
            message: trackResponse?.message,
          })
          return acc
        }

        if (
          !trackResponse.metadata ||
          trackResponse.metadata?.ai_transcription_in_progress
        ) {
          acc.pending.push(track)
          dispatch(handleLyricsStarting(id))
        } else {
          dispatch(
            handleLyricsComplete(
              id,
              track.name,
              trackResponse.lyrics,
              trackResponse.metadata,
            ),
          )
        }

        return acc
      },
      {
        pending: [],
        failures: [],
      },
    )

    dispatch(
      updateBusinessPrivateMetadata({
        lyric_transcription_quota_left: response.lyric_transcription_quota_left,
      }),
    )

    if (result.pending.length === 1 && result.pending[0]) {
      pushUINotification({
        uid: `lyric-transcription-pending-${result.pending[0].id}`,
        autoDismiss: 5,
        level: 'success',
        title: 'Lyrics generating...',
        message: `We’re transcribing the track ‘${result.pending[0].name}’.`,
      })
    } else if (result.pending.length > 1) {
      pushUINotification({
        autoDismiss: 5,
        level: 'success',
        title: 'Lyrics generating...',
        message: `We’re transcribing the selected tracks.`,
      })
    }

    if (result.failures.length === 1 && result.failures[0]) {
      pushUINotification({
        level: 'error',
        title: 'Something went wrong!',
        message: `We couldn’t generate the lyrics for ‘${result.failures[0].track.name}’ at this point. ${result.failures[0].message}`,
      })
    } else if (result.failures.length > 1) {
      pushUINotification({
        level: 'error',
        title: 'Something went wrong!',
        message: `We couldn’t generate the lyrics for some of the selected tracks at this point.`,
      })
    }
  }
}
/**
 * Sends feedback about AI-generated lyrics transcription quality.
 * @param trackId - ID of the track with transcribed lyrics
 * @param feedback - Whether the transcription was good or bad
 * @param reason - Optional predefined reason for negative feedback
 * @param otherReason - Optional custom reason for negative feedback
 * @returns Thunk that sends feedback and updates track if negative
 */
export function sendTranscriptionFeedback(
  trackId: TrackId,
  feedback: 'positive' | 'negative',
  reason?: string,
  otherReason?: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: TRANSCRIPTION_FEEDBACK_SEND,
      trackId,
    })
    await api.sendTranscriptionFeedback(trackId, feedback, reason, otherReason)
    const track = getState().tracks[trackId]
    // remove lyrics if feedback is negative
    const metadata = { ...track.metadata }
    if (feedback === 'negative') delete metadata.Lyrics
    const updatedTrack: Track = {
      ...track,
      metadata,
      metadata_extras: {
        ...track.metadata_extras,
        Lyrics: { ...track.metadata_extras.Lyrics, source: 'user' },
      },
    }
    dispatch({
      type: TRACK_LOAD_BATCH,
      list: [updatedTrack],
    })
  }
}
/**
 * Deletes a specific version of a playlist.
 * @param playlist - The playlist version to delete
 * @returns Thunk that deletes the version and switches to previous version or removes playlist
 */
export function deletePlaylistVersion(
  playlist: Playlist,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { id } = playlist
    dispatch({
      type: PLAYLIST_VERSION_DELETE,
    })
    api.deletePlaylistVersion(id).then(
      resp => {
        dispatch({
          type: PLAYLIST_VERSION_DELETE_SUCCESS,
          playlist: playlist,
        })

        // if previous version is available switch to previous version,
        // otherwise remove playlist
        // @ts-expect-error - fix later
        if (resp) {
          mapResponseToReducerObjects(dispatch, getState, resp)
        } else {
          dispatch({
            type: PLAYLIST_DELETE_BATCH_SUCCESS,
            playlistIds: [playlist.playlist_id],
          })
        }
      },
      error => {
        dispatch({
          type: PLAYLIST_VERSION_DELETE_ERROR,
          error,
        })
      },
    )
  }
}
/**
 * Deletes a specific format/file from a track.
 * @param track - The track containing the format to delete
 * @param format - The format identifier to delete
 * @returns Thunk that deletes the track format
 */
export function deleteTrackFormat(
  track: Track,
  format: string,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    const { id } = track
    dispatch({
      type: TRACK_FORMAT_DELETE,
      track,
      format,
    })
    api
      .deleteTrackFormat(id, format)
      .then(() => {
        dispatch({
          type: TRACK_FORMAT_DELETE_SUCCESS,
          track,
          format,
        })
      })
      .catch(error => {
        dispatch({
          type: TRACK_FORMAT_DELETE_ERROR,
          error,
        })
      })
  }
}
/**
 * Creates or updates a writer split for publishing/royalty purposes.
 * @param split - Split data including writer, publisher, PRO, and percentage
 * @param _trackId - Track ID (currently unused but kept for compatibility)
 * @param IS_UPDATE - Whether this is an update operation or new creation
 * @returns Thunk that creates/updates split and manages related entities
 */
export function onlyCreateWriterSplit( // TODO: add Split type
  split: Record<string, any>,
  _trackId: TrackId,
  IS_UPDATE?: boolean,
): (
  dispatch: any,
  getState: GetState,
) => Promise<{
  id: any
  pro: any
  publisher: any
  split: any
  writer: any
}> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // TODO: confirm and question that backend lost ability to add splits to multiple tracks in single call?
    // temporarily convert request until clarification will be provided
    let results = []

    if (IS_UPDATE) {
      results = await api.updateWriterSplit(split.id, split)
    } else {
      results = await api.addTrackWriter({
        id: split.id,
        writer: split.writer,
        publisher: split.publisher,
        pro: split.pro,
        split: split.split,
        track_ids: split.track_ids,
      })
    }

    // @ts-expect-error - fix later
    const result = results.results[0]
    const contact = await api.getSingleContact(result.writer.id)
    const { pro, publishers } = getState().entities
    dispatch({
      type: CONTACTS_CREATE_SUCCESS,
      contact,
    })

    if (result.pro) {
      const newPro = {
        id: result.pro.id,
        name: split.pro.name,
      }
      const isNewPro = !pro.find(item => item.id === newPro.id)

      if (isNewPro) {
        dispatch(addPro([newPro]))
      }
    }

    if (result.publisher) {
      const newPublisher = {
        id: result.publisher.id,
        name: split.publisher.name,
      }
      const isNewPublisher = !publishers.find(
        item => item.id === newPublisher.id,
      )

      if (isNewPublisher) {
        dispatch(addPublishers([newPublisher]))
      }
    }

    return {
      id: result.id,
      writer: result.writer.id,
      split: result.split,
      publisher: result.publisher?.id,
      pro: result.pro?.id,
    }
  }
}
/**
 * Adds a writer split to tracks for publishing/royalty tracking.
 * @param split - Split data including track IDs, writer, publisher, and percentage
 * @returns Thunk that adds writer split and shows success notification
 */
export function addWriterSplit( // TODO: add Split type
  split: Record<string, any>,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    const { track_ids, tracks, ...writer } = split
    dispatch({
      type: TRACK_ADD_WRITER,
      track: track_ids[0],
      writer,
    })

    try {
      const resultWriter = await dispatch(
        onlyCreateWriterSplit(split, track_ids[0]),
      )
      dispatch({
        type: TRACK_ADD_WRITER_SUCCESS,
        track: track_ids[0],
        writer: resultWriter,
        meta: {
          mixpanel: {
            event:
              track_ids.length > 1
                ? 'Bulk add writer split'
                : 'Add writer split',
          },
        },
      })
      pushUINotification({
        message: 'Writer added!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: TRACK_ADD_WRITER_FAIL,
        track: track_ids[0],
        tracks,
        writer: omit(split, ['track', 'tracks']),
        error,
      })
      throw error
    }
  }
}
/**
 * Deletes a writer split from a track.
 * @param trackId - ID of the track to remove split from
 * @param splitId - ID of the split to delete
 * @returns Thunk that deletes the split and shows warning notification
 */
export function deleteWriterSplit(
  trackId: TrackId,
  splitId: number,
): (dispatch: any) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: TRACK_DELETE_WRITER,
      trackId,
      splitId,
    })

    try {
      await api.deleteWriterSplit(splitId)
      pushUINotification({
        message: 'Writer removed!',
        level: 'warning',
      })
      return dispatch({
        type: TRACK_DELETE_WRITER_SUCCESS,
        trackId,
        splitId,
        meta: {
          mixpanel: {
            event: 'Delete writer split',
          },
        },
      })
    } catch (error) {
      dispatch({
        type: TRACK_DELETE_WRITER_FAIL,
        trackId,
        splitId,
        error,
      })
    }
  }
}
/**
 * Updates an existing writer split with new data.
 * @param split - Updated split data including ID and new values
 * @returns Thunk that updates the split and shows success notification
 */
export function updateWriterSplit( // TODO: add Split type
  split: Record<string, any>,
): (dispatch: any) => Promise<void> {
  const { track_ids } = split
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: TRACK_UPDATE_WRITER,
      track: track_ids[0],
      split,
    })

    try {
      const IS_UPDATE = true
      const resultWriter = await dispatch(
        onlyCreateWriterSplit(split, track_ids[0], IS_UPDATE),
      )
      dispatch({
        type: TRACK_UPDATE_WRITER_SUCCESS,
        track: track_ids[0],
        split: resultWriter,
        meta: {
          mixpanel: {
            event: 'Edit writer split',
          },
        },
      })
      pushUINotification({
        message: 'Split edited!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: TRACK_UPDATE_WRITER_FAIL,
        track: track_ids[0],
        split,
        error,
      })
      throw error
    }
  }
}
/**
 * Updates territory/group assignments for multiple tracks.
 * @param trackIds - Array of track IDs to update
 * @param addGroups - Groups/territories to add to the tracks
 * @param removeGroups - Groups/territories to remove from the tracks
 * @returns Thunk that updates track territories and handles access validation
 */
export const bulkSaveTerritories =
  (
    trackIds: TrackId[],
    addGroups: Group[],
    removeGroups: Group[],
  ): ((dispatch: any, getState: GetState) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const {
      user: { territories },
      tracks: reduxTracks,
    } = getState()

    try {
      dispatch({
        type: TRACK_GROUP_CHANGE,
        trackIds,
        addGroups,
        removeGroups,
      })
      const tracks = trackIds.map(id => reduxTracks[id])
      const results = await api.alterTrackGroups(
        tracks,
        addGroups,
        removeGroups,
      )
      const updatedTracks = tracks.map(t => {
        const updatedTrack = results.find(r => r.id === t.id)
        return {
          ...t,
          groups: updatedTrack ? updatedTrack.groups : t.groups,
          outside_your_territories: updatedTrack
            ? updatedTrack.outside_your_territories
            : t.outside_your_territories,
        }
      })
      const commonGroups = getCommonGroups(updatedTracks)
      dispatch({
        type: TRACK_LOAD_BATCH,
        list: updatedTracks,
      })
      const userHasAccessToTerritory = territories.find(id =>
        addGroups.find(g => g.id === id),
      )
      if (!userHasAccessToTerritory) NavigationService.replace('/')
      pushUINotification({
        message: `Saved!`,
        level: 'success',
      })
      return dispatch({
        type: TRACK_GROUP_CHANGE_SUCCESS,
        trackIds,
        addGroups,
        removeGroups,
        commonGroups,
      })
    } catch (error) {
      dispatch(sendGenericError(error))
      dispatch({
        type: TRACK_GROUP_CHANGE_ERROR,
        trackIds,
        addGroups,
        removeGroups,
        error,
      })
    }
  }
